import type { Point2d } from "@logic/lib/2d"
import { doesPolygon, isPointInPolygon } from "@logic/lib/2d"
import type { Panel } from "@logic/lib/design"
import { screenToStudio, studioToScreen } from "@logic/lib/design"
import type { Face2dComputed } from "@logic/lib/mesh"
import type { ModuleInverterCompatibilityData } from "@logic/lib/powur-os"
import { scaleInchesVectorToImageMeters } from "@logic/lib/utils"
import { atom, atomFamily, DefaultValue, selector, selectorFamily } from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { siteLocalState } from "~/containers/Proposal/state/site/site-state"
import { selectObject } from "~/utils/recoil"

import { panelBeingDraggedState } from "./dragging"
import { panelSelectionRotationState } from "./rotating"
import { panelSnapTranslocationState } from "./snapping"
import { findPanelValidityState } from "./validation"
import { preferredImageState } from "../../imageElements"
import { studioMouseState } from "../../inputs"
import { zoomState } from "../../view"
import { areaIndex, findAreaState, findProjectedAreaState } from "../areas"
import { areasOfPanelsIndexState } from "../relations"

export * from "./snapping"

//¯¯¯¯¯¯¯¯¯¯//
// SETTINGS //
//__________//
export const DEFAULT_SELECTED_MODULE: {
  id: number
  visionId: string
  height: number
  width: number
  name: string
  manufacturer: string
  isDefault: boolean
  currentProposalPrice: number
  wattage: number
  moduleData: ModuleInverterCompatibilityData | null
} = {
  id: NaN,
  visionId: ``,
  height: NaN,
  width: NaN,
  name: ``,
  manufacturer: ``,
  isDefault: false,
  currentProposalPrice: 0,
  wattage: 0,
  moduleData: null,
}
export const selectedModuleRemoteState = atom<typeof DEFAULT_SELECTED_MODULE>({
  key: `selectedModuleRemote_OS`,
  default: DEFAULT_SELECTED_MODULE,
})
export const selectedModuleState = atom<typeof DEFAULT_SELECTED_MODULE>({
  key: `selectedModule_OS`,
  default: DEFAULT_SELECTED_MODULE,
})
export const isSelectedModuleSavedState = selector<boolean>({
  key: `isSelectedModuleSaved`,
  get: ({ get }) => {
    const selectedModule = get(selectedModuleState)
    const selectedModuleRemote = get(selectedModuleRemoteState)

    return selectedModule.id === selectedModuleRemote.id
  },
})

type FillTypes = `Opaque` | `Transparent`

export const panelFillState = atom<FillTypes>({
  key: `panelFill`,
  default: `Opaque`,
})

export const panelGapState = atom<number>({
  key: `panelGap`,
  default: 1,
})

export const panelGridGapState = selector<number>({
  key: `panelGridGap`,
  get: ({ get }) => {
    const zoom = get(zoomState)
    const gap = get(panelGapState)
    const { zoomLevel: imageZoom } = get(preferredImageState)
    const { latitude } = get(siteLocalState)
    const gridGap = scaleInchesVectorToImageMeters({
      vector: { w: gap, h: gap },
      latitude,
      zoomLevel: imageZoom,
    })

    return gridGap.h * (zoom / 100)
  },
})

//¯¯¯¯¯¯//
// DATA //
//______//
export const DEFAULT_PANEL: Panel = {
  x: 0,
  y: 0,
  w: 0,
  h: 0,
  r: 0,
  xSnap: null,
  ySnap: null,
  wOriginal: 0,
  hOriginal: 0,
  isActive: true,
  orientation: 0,
  module: null,
  moduleOsId: null,
}

export const panelIndex = atom<string[]>({
  key: `panelIndex`,
  default: [],
})
export const findPanelState = atomFamily<Panel, string>({
  key: `panel`,
  default: DEFAULT_PANEL,
})
export const allPanelsState = selectObject(
  `panel_design`,
  findPanelState,
  panelIndex,
)

export const findProjectedPanelState = selectorFamily<Panel, string>({
  key: `projectedPanel`,
  get:
    (panelId) =>
    ({ get }) => {
      const panel = get(findPanelState(panelId))
      const zoom = get(zoomState)
      const projected = studioToScreen.panel(panel, zoom)

      return { ...panel, ...projected }
    },
  set:
    (panelId) =>
    ({ get, set }, newValue) => {
      if (newValue instanceof DefaultValue) {
        return console.warn(`tried to set panel ${panelId} to DefaultValue`)
      }
      const zoom = get(zoomState)
      set(findPanelState(panelId), (current) => ({
        ...current,
        ...screenToStudio.panel(newValue, zoom),
      }))
    },
})

export const panelSelectionState = atom<Set<string>>({
  key: `panelSelection`,
  default: new Set(),
})

export const filteredPanelSelectionState = selector<Set<string>>({
  key: `panelSelectionCenter`,
  get: ({ get }) => {
    const filtered: Set<string> = new Set()
    const draggedPanel = get(panelBeingDraggedState)
    if (!draggedPanel) return filtered
    const installAreasToPanels = get(areasOfPanelsIndexState)
    const area = installAreasToPanels._alt.get(draggedPanel)
    const selection = get(panelSelectionState)

    selection.forEach((panelId) => {
      const cur = installAreasToPanels._alt.get(panelId)
      if (!area && !cur) filtered.add(panelId)
      else if (cur === area || cur === undefined) filtered.add(panelId)
    })

    return filtered
  },
})

export const panelSelectionPositionState = selector<Point2d>({
  key: `panelSelectionPosition`,
  get: () => ({ x: NaN, y: NaN }),
  set: (transactors, displacement) => {
    const { get, set } = transactors
    if (displacement instanceof DefaultValue) return

    const zoom = get(zoomState)
    const mouse = get(studioMouseState)
    const mouseProjected = screenToStudio.point(mouse, zoom)
    const { offset, shouldSnap } = get(panelSnapTranslocationState)
    // console.log({ offset, displacement })
    set(panelSelectionRotationState, {
      origin: mouseProjected,
      willReorient90: false,
      displacement,
      offset,
      shouldSnap,
      dragging: true,
    })

    const selection = get(filteredPanelSelectionState)

    selection.forEach((panelId) => {
      const installAreaIds = get(areaIndex)

      const tuplesForAreasContainingMouse = installAreaIds
        .map((areaId): [string, Face2dComputed] => [
          areaId,
          get(findProjectedAreaState(areaId)),
        ])
        .filter(([, area]) => isPointInPolygon(mouse, area))

      switch (tuplesForAreasContainingMouse.length) {
        case 0:
          set(areasOfPanelsIndexState, (current) => {
            current.delete(undefined, panelId)

            return current
          })
          break
        case 1:
          set(areasOfPanelsIndexState, (current) => {
            const [onlyAreaTuple] = tuplesForAreasContainingMouse
            const [areaId] = onlyAreaTuple
            current.set(areaId, panelId)

            return current
          })
          break
        default:
          set(areasOfPanelsIndexState, (current) => {
            const tupleForInnermostArea = tuplesForAreasContainingMouse.find(
              ([areaId, area]) =>
                tuplesForAreasContainingMouse.every(
                  ([otherAreaId, otherArea]) =>
                    areaId === otherAreaId ||
                    doesPolygon(otherArea).containPolygon(area),
                ),
            )
            if (tupleForInnermostArea === undefined) {
              throw new Error(`Strange error: unclear which area contains mouse`)
            }
            const [areaId] = tupleForInnermostArea
            current.set(areaId, panelId)

            return current
          })
      }
    })
  },
})

export const validPanelIndex = selector<string[]>({
  key: `validPanelIds`,
  get: ({ get }) => {
    const panelIds = get(panelIndex)
    const validIds = panelIds.filter(
      (panelId) => get(findPanelValidityState(panelId)) !== `Error`,
    )

    return validIds
  },
})

export const activePanelIndex = selector<string[]>({
  key: `numberOfActivePanels`,
  get: ({ get }) => {
    const validIds = get(validPanelIndex)
    const activeIds = validIds.filter(
      (panelId) => get(findPanelState(panelId)).isActive,
    )

    return activeIds
  },
})

export const hoveredPanelState = atom<string>({
  key: `hoveredPanelState`,
  default: ``,
})

export const findPanelSlope = selectorFamily<number, string>({
  key: `panelSlope`,
  get:
    (id) =>
    ({ get }) => {
      const areasOfPanels = get(areasOfPanelsIndexState)
      const areaContainingPanelId = areasOfPanels.from(id)

      return areaContainingPanelId
        ? get(findAreaState(areaContainingPanelId)).slope
        : 0
    },
})
